import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import BLOG_ARTICLES from '../data/blogArticles';

const categoryColor = {
  Research: 'text-emerald-500 bg-emerald-500/[0.08] border-emerald-500/25',
  Announcement: 'text-violet-400 bg-violet-500/[0.08] border-violet-500/20',
  Enterprise: 'text-blue-400 bg-blue-500/[0.08] border-blue-500/20',
  Developers: 'text-green-400 bg-green-500/[0.08] border-green-500/20',
  Engineering: 'text-amber-400 bg-amber-500/[0.08] border-amber-500/20',
  Company: 'text-rose-400 bg-rose-500/[0.08] border-rose-500/20',
};

export default function BlogPage() {
  const featured = BLOG_ARTICLES[0];
  const rest = BLOG_ARTICLES.slice(1);

  useEffect(() => {
    document.title = 'Blog | OpenSov AI - Insights on AI Technology and Innovation';

    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (property.startsWith('og:')) el.setAttribute('property', property);
        else el.setAttribute('name', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Explore the latest insights on AI technology, product updates, enterprise AI adoption, and thought leadership from the OpenSov AI team.');
    setMeta('og:title', 'Blog | OpenSov AI');
    setMeta('og:description', 'Insights on AI technology, product updates, and thought leadership from OpenSov AI.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://opensov.com/blog');

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'OpenSov AI Blog',
      description: 'Insights on AI technology, product updates, and thought leadership from the OpenSov AI team.',
      url: 'https://opensov.com/blog',
      publisher: {
        '@type': 'Organization', name: 'OpenSov AI',
        logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_9c3ced01-7873-4460-b2ba-221ae55b6a5f/artifacts/palgt8p6_OpenSov%20Logo%20.png' },
      },
      blogPost: BLOG_ARTICLES.map(a => ({
        '@type': 'BlogPosting',
        headline: a.title,
        description: a.excerpt,
        datePublished: a.date,
        author: { '@type': 'Organization', name: 'OpenSov AI Team' },
        url: `https://opensov.com/blog/${a.slug}`,
        image: a.image,
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-jsonld';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('blog-jsonld');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-app text-content font-sans">
      <Navbar />

      <main className="pt-28 pb-20" data-testid="blog-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-16"
          >
            <p className="text-emerald-500 text-[10px] font-mono tracking-[0.3em] uppercase mb-4 font-bold">Blog</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-content tracking-tighter" data-testid="blog-heading">
              Insights & Updates
            </h1>
            <p className="mt-4 text-content-2 font-sora text-base md:text-lg max-w-2xl leading-relaxed">
              Thoughts on AI technology, product announcements, and perspectives on the future of intelligence.
            </p>
          </motion.div>

          {/* Featured Article */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-16"
          >
            <Link to={`/blog/${featured.slug}`} className="group block" data-testid="featured-article">
              <article className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="relative aspect-[16/10] rounded-2xl overflow-hidden border border-line">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <div className="py-2">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${categoryColor[featured.category] || 'text-content-2 border-line-2 bg-overlay'}`}>
                      {featured.category}
                    </span>
                    <span className="text-content-3 text-[11px] font-mono">Featured</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-content tracking-tight mb-4 group-hover:text-emerald-500 transition-colors duration-300 leading-tight">
                    {featured.title}
                  </h2>
                  <p className="text-content-2 font-sora text-sm leading-relaxed mb-5 line-clamp-3">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-content-3 text-[12px] font-sora mb-6">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{new Date(featured.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{featured.readTime}</span>
                  </div>
                  <span className="inline-flex items-center gap-2 text-emerald-500 text-sm font-sora font-medium group-hover:gap-3 transition-all duration-300">
                    Read article <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </article>
            </Link>
          </motion.div>

          <div className="h-px bg-overlay mb-16" />

          {/* Article Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((article, idx) => (
              <motion.div key={article.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 + idx * 0.06 }}>
                <Link to={`/blog/${article.slug}`} className="group block h-full" data-testid={`article-card-${idx}`}>
                  <article className="h-full flex flex-col">
                    <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-line mb-5">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${categoryColor[article.category] || 'text-content-2 border-line-2 bg-overlay'}`}>
                        {article.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-content tracking-tight mb-2 group-hover:text-emerald-500 transition-colors duration-300 leading-snug">{article.title}</h3>
                    <p className="text-content-3 font-sora text-sm leading-relaxed mb-4 line-clamp-2 flex-1">{article.excerpt}</p>
                    <div className="flex items-center gap-4 text-content-3 text-[11px] font-sora mt-auto">
                      <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{article.readTime}</span>
                    </div>
                  </article>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
